import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Settings, Webhook, Home, Users, Database, Plug,
  DownloadCloud, Archive, RotateCcw, Clock, Sliders, FileText, HelpCircle,
  MousePointerClick, PanelTopOpen, Sparkles, Menu, X, LogOut, KeyRound, CreditCard, Briefcase,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useBilling } from '../lib/billing';
import { PLAN_NAMES, getPlanStatus } from '../lib/plans';
import WorkspaceSwitcher from './WorkspaceSwitcher';

interface NavItem {
  path: string;
  icon: typeof LayoutDashboard;
  label: string;
}

interface NavGroup {
  label: string;
  showLabel: boolean;
  items: NavItem[];
}

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { billing, isAgency } = useBilling();
  const isAdmin = user?.isAdmin === true;
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const groups: NavGroup[] = [
    {
      label: 'Main', showLabel: false, items: [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/webhooks', icon: Webhook, label: 'Webhooks' },
        { path: '/forms', icon: FileText, label: 'Forms' },
        { path: '/quizzes', icon: HelpCircle, label: 'Quizzes' },
        { path: '/buttons', icon: MousePointerClick, label: 'Buttons' },
        { path: '/popups', icon: PanelTopOpen, label: 'Popups' },
        { path: '/ai-connector', icon: Sparkles, label: 'AI Connector' },
        { path: '/teams', icon: Users, label: 'Teams' },
        { path: '/api-keys', icon: KeyRound, label: 'API Keys' },
        ...(isAgency ? [{ path: '/workspaces', icon: Briefcase, label: 'Workspaces' }] : []),
        { path: '/billing', icon: CreditCard, label: 'Billing' },
        { path: '/settings', icon: Settings, label: 'Settings' },
      ],
    },
    {
      label: 'Vault', showLabel: true, items: [
        { path: '/vault', icon: Database, label: 'Vault Dashboard' },
        { path: '/vault/connections', icon: Plug, label: 'Connections' },
        { path: '/vault/backups', icon: DownloadCloud, label: 'Backups' },
        { path: '/vault/snapshots', icon: Archive, label: 'Snapshots' },
        { path: '/vault/restores', icon: RotateCcw, label: 'Restores' },
        { path: '/vault/schedules', icon: Clock, label: 'Schedules' },
      ],
    },
    ...(isAdmin ? [{
      label: 'Admin', showLabel: true, items: [
        { path: '/admin/homepage', icon: Home, label: 'Homepage Editor' },
        { path: '/admin/users', icon: Users, label: 'User Management' },
        { path: '/admin/system-settings', icon: Sliders, label: 'System Settings' },
      ],
    }] : []),
  ];

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';

  // Clerk signOut + clear the workspace selection; redirects to /login.
  const handleLogout = () => logout();

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-3 left-3 z-50 p-2 bg-surface rounded-lg shadow-card-sm border border-line text-ink-2"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {open && <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} />}

      <aside
        className={`fixed top-0 left-0 h-screen w-[252px] z-50 flex flex-col bg-[var(--sidebar)] border-r border-line
          transition-transform duration-200 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        {/* Brand */}
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4">
          <Link to="/dashboard" className="flex items-center min-w-0">
            <img src="/logo.png" alt="My Lead Syncer" className="h-8 w-auto max-w-full" />
          </Link>
          <button onClick={() => setOpen(false)} className="md:hidden p-1 text-ink-3 hover:text-ink flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto pb-3.5 pt-1.5">
          {groups.map((grp) => (
            <div key={grp.label}>
              {grp.showLabel && (
                <div className="px-[22px] pt-3.5 pb-[7px] font-bold text-[10px] text-ink-3 uppercase tracking-[0.13em]">
                  {grp.label}
                </div>
              )}
              {grp.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative flex items-center gap-[11px] px-[22px] py-[9px] text-[13.5px] font-semibold transition-colors
                      ${active ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-surface-2 hover:text-ink'}`}
                  >
                    {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-accent" />}
                    <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3.5 border-t border-line">
          {billing && (() => {
            const status = getPlanStatus(billing);
            return (
              <div
                className="relative overflow-hidden rounded-[13px] p-3.5 text-white"
                style={{ background: 'linear-gradient(150deg,var(--accent),var(--accent-hover))' }}
              >
                <div className="absolute -right-[18px] -top-[18px] w-[74px] h-[74px] rounded-full bg-white/15" />
                <div className="relative font-display font-bold text-[13px]">
                  {PLAN_NAMES[billing.plan] ?? billing.plan} plan
                </div>
                <div className="relative font-medium text-[11.5px] opacity-85 mt-0.5 leading-snug">
                  {billing.isWorkspace ? 'Managed by your agency' : status.label}
                </div>
                {status.upsell && billing.canManageBilling ? (
                  <Link
                    to="/billing"
                    className="relative block text-center mt-2.5 w-full py-1.5 rounded-lg bg-white text-accent-ink font-bold text-[12px] hover:bg-white/90 transition"
                  >
                    Upgrade
                  </Link>
                ) : status.key === 'past_due' && billing.canManageBilling ? (
                  <Link
                    to="/billing"
                    className="relative block text-center mt-2.5 w-full py-1.5 rounded-lg bg-white text-danger font-bold text-[12px] hover:bg-white/90 transition"
                  >
                    Fix payment
                  </Link>
                ) : null}
              </div>
            );
          })()}
          {isAgency && (
            <div className="mt-2.5">
              <WorkspaceSwitcher />
            </div>
          )}
          <div className="flex items-center gap-2.5 px-1.5 pt-2.5 pb-0.5">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[12px] flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#1e293b,#475569)' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[12.5px] text-ink truncate">{fullName}</div>
              <div className="font-medium text-[11px] text-ink-3 truncate">{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              title="Log out"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-3 hover:text-danger hover:bg-danger-soft transition flex-shrink-0"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
